import type {
  Dashboard409ChartMetricSeriesQuery,
  Dashboard409MetricsCatalogQuery,
  Dashboard409SummaryMetricSeriesQuery,
} from './generated/graphql';

type SummaryMetricSeries = Dashboard409SummaryMetricSeriesQuery['metric_series'];
type ChartMetricSeries = Dashboard409ChartMetricSeriesQuery['metric_series'];
type SummaryCompany = SummaryMetricSeries['companies'][number];
type SummaryDataPoint = SummaryCompany['data_points'][number];
type ChartDataPoint = ChartMetricSeries['companies'][number]['data_points'][number];

// Company metadata used by table/bar labels. Alexandria now nests observations
// per company; chart responses omit company_name, so treat it as optional.
export type MetricSeriesCompany = Pick<SummaryCompany, 'requested_identifier'> &
  Partial<Pick<SummaryCompany, 'company_name'>>;

// Shared internal shape: the query layer flattens each company's data_points
// into one list, tagging every point with its company's requested_identifier.
// Summary points include the source observation date; chart points intentionally
// omit it to keep range responses small.
export type MetricSeriesPoint = ChartDataPoint & { requested_identifier: string } &
  Partial<Pick<SummaryDataPoint, 'as_of_date'>>;

export type MetricSeriesResult = {
  companies?: MetricSeriesCompany[];
  points: MetricSeriesPoint[];
  unresolved_tickers: SummaryMetricSeries['unresolved_tickers'];
};

export const normalizeMetricSeries = (series: SummaryMetricSeries | ChartMetricSeries): MetricSeriesResult => ({
  companies: series.companies.map(company => ({
    requested_identifier: company.requested_identifier,
    company_name: 'company_name' in company ? company.company_name : null,
  })),
  points: series.companies.flatMap(company =>
    company.data_points.map(point => ({
      ...point,
      requested_identifier: company.requested_identifier,
    }))
  ),
  unresolved_tickers: series.unresolved_tickers,
});

export type MetricCatalogEntry = Dashboard409MetricsCatalogQuery['metrics_catalog'][number];

// metric_key -> unit semantics from the catalog, parsed for consumers.
export type MetricUnitsByKey = Record<string, { unit: string | null; factor: number | null }>;

// Metric keys from Alexandria's catalog used by the Public Comps section.
export const ALEXANDRIA_METRICS = {
  ENTERPRISE_VALUE: 'enterprise_value',
  LTM_REVENUE: 'ltm_revenue',
  NTM_REVENUE: 'ntm_revenue',
  LTM_EBITDA: 'ltm_ebitda',
  NTM_EBITDA: 'ntm_ebitda',
  GROSS_MARGIN: 'gross_margin',
  EBITDA_MARGIN: 'ebitda_margin',
  LTM_REVENUE_GROWTH: 'ltm_revenue_growth',
  ESTIMATED_REVENUE_GROWTH: 'estimated_revenue_growth',
} as const;
